import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FamilyPassBalance, PassProduct, PassProductInput } from '@pirouette/shared';
import { passProductInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

export type FamilyPassBalanceWithProduct = FamilyPassBalance & {
  pass_products?: Pick<
    PassProduct,
    'id' | 'name' | 'kind' | 'included_uses' | 'valid_days' | 'price_cents' | 'active'
  > | null;
};

export function usePassProducts() {
  return useQuery({
    queryKey: ['pass-products'],
    queryFn: async (): Promise<PassProduct[]> => {
      const { data, error } = await supabase
        .from('pass_products')
        .select('*')
        .eq('active', true)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useFamilyPassBalances(familyId: string | undefined) {
  return useQuery({
    enabled: !!familyId,
    queryKey: ['family-pass-balances', familyId],
    queryFn: async (): Promise<FamilyPassBalanceWithProduct[]> => {
      const { data, error } = await supabase
        .from('family_pass_balances')
        .select('*, pass_products(id, name, kind, included_uses, valid_days, price_cents, active)')
        .eq('family_id', familyId!)
        .eq('active', true)
        .or(`valid_until.is.null,valid_until.gte.${new Date().toISOString().slice(0, 10)}`)
        .order('valid_until', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreatePassProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PassProductInput): Promise<PassProduct> => {
      const parsed = passProductInputSchema.parse(input);
      const { data, error } = await supabase
        .from('pass_products')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pass-products'] });
    },
  });
}
